import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import userService from '../services/userService';

const TShirtSizeManagement = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch t-shirt sizes on component mount
  useEffect(() => {
    fetchTShirtSizes();
  }, []);

  const fetchTShirtSizes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getTShirtSizes();
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch t-shirt sizes');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await userService.exportTShirtSizes();
    } catch (err) {
      setError(err.message || 'Failed to export t-shirt sizes');
    } finally {
      setExporting(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.tShirtSize?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate statistics
  const stats = {
    total: users.length,
    provided: users.filter((u) => u.tShirtSize).length,
    notProvided: users.filter((u) => !u.tShirtSize).length,
  };

  // Count by size
  const sizeDistribution = users.reduce((acc, u) => {
    if (u.tShirtSize) {
      acc[u.tShirtSize] = (acc[u.tShirtSize] || 0) + 1;
    }
    return acc;
  }, {});

  // Paginate filtered users
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Check if user is admin
  if (user?.systemRole !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          T-Shirt Size Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || loading}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Size Provided
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {stats.provided}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {stats.notProvided}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Size Distribution */}
      {Object.keys(sizeDistribution).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Size Distribution
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => (
              <Chip
                key={size}
                label={`${size}: ${sizeDistribution[size] || 0}`}
                color={sizeDistribution[size] ? 'primary' : 'default'}
                variant={sizeDistribution[size] ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by name, email, or size..."
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Users Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>T-Shirt Size</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((u) => (
                    <TableRow key={u._id} hover>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.tShirtSize ? (
                          <Chip label={u.tShirtSize} color="primary" size="small" />
                        ) : (
                          <Chip label="Not Provided" color="default" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {u.tShirtSize ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="warning" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default TShirtSizeManagement;
